import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  EventArgs,
  FilteredGraphWrapper,
  GraphComponent,
  GraphInputMode,
  GraphItemTypes,
  GraphViewerInputMode,
  HierarchicalNestingPolicy,
  HoveredItemChangedEventArgs,
  Command,
  IEdge,
  IGraph,
  ILabel,
  IModelItem,
  INode,
  ItemClickedEventArgs,
  InputModeItemEventArgs,
  LayoutOrientation,
  Point,
  ScrollBarVisibility,
  TimeSpan,
} from '@ardoq/yfiles';
import {
  addCanvasClickedListener,
  addGroupCollapsedListener,
  addGroupExpandedListener,
  addHoveredItemChangedListener,
  addItemClickedListener,
  addItemDoubleClickedListener,
  calculateAndApplyIncrementalHierarchicLayout,
  calculateAndApplyInitialHierarchicLayout,
  centerViewportOnFocusedItem,
  collapseAllGroups,
  expandAllGroups,
  getCollapsedViewGroups,
  getEdgeCountMap,
  getGraphItemsById,
  populateGraph,
  removeCanvasClickedListener,
  removeGroupCollapsedListener,
  removeGroupExpandedListener,
  removeHoveredItemChangedListener,
  removeItemClickedListener,
  removeItemDoubleClickedListener,
  resetNodeToDefaultCollapsedSize,
  selectGraphItemsById,
  translateGraphPropsToButtonConfigProps,
  updateEdgeLabels,
} from './utils';
import {
  OnItemDoubleClick,
  OnItemFocus,
  SimpleBlockDiagramEdge,
  SimpleBlockDiagramLabels,
  SimpleBlockDiagramNode,
} from './types';
import { isEqual } from 'lodash';
import { useEffectOnce } from '@ardoq/hooks';
import { getTooltip } from './customYFilesImplementations/utils';
import { GhostButton } from '@ardoq/button';
import { ZoomInIcon, ZoomOutIcon } from '@ardoq/icons';
import styled from 'styled-components';
import { s8 } from '@ardoq/design-tokens';
import { SettingsBarButton } from '@ardoq/settings-bar';
import { ButtonConfig } from '@ardoq/view-settings';
import { dispatchAction, connect } from '@ardoq/rxbeach';
import { collapseExpandAllGroupsButtonsPropsChange } from './actions';
import { collapseExpandAllGroupsButtonProps$ } from './collapseExpandAllGroupsButtonProps$';
import { GroupingCollapseState } from '@ardoq/api-types';

const initializeTooltips = (graphComponent: GraphComponent) => {
  if (!(graphComponent.inputMode instanceof GraphInputMode)) return;

  const toolTipInputMode = graphComponent.inputMode.toolTipInputMode;

  graphComponent.inputMode.toolTipItems =
    GraphItemTypes.NODE | GraphItemTypes.EDGE | GraphItemTypes.LABEL;

  graphComponent.inputMode.addEventListener('query-item-tool-tip', getTooltip);

  toolTipInputMode.toolTipLocationOffset = new Point(10, 10);
  toolTipInputMode.delay = TimeSpan.fromMilliseconds(500);
  toolTipInputMode.duration = TimeSpan.fromSeconds(5);
};

type Props = {
  graph: IGraph | null;
  nodes: SimpleBlockDiagramNode[];
  edges: SimpleBlockDiagramEdge[];
  inputMode: GraphViewerInputMode | null;
  className?: string;
  layoutOrientation: LayoutOrientation;
  defaultGroupingCollapseState: GroupingCollapseState;
  edgeGrouping: boolean;
  focusedItemId?: string;
  centeredItemId?: string;
  onItemFocus?: OnItemFocus;
  onItemDoubleClick?: OnItemDoubleClick;
  onCanvasClick?: VoidFunction;
  registerVisualizationContainer?: (container: HTMLElement) => void;
  unregisterVisualizationContainer?: () => void;
  registerGraphComponent?: (graphComponent: GraphComponent) => void;
  unregisterGraphComponent?: () => void;
  animateLayout?: boolean;
  hasZoomControls?: boolean;
  lightenDarkColors: boolean;
  showLeftMenu?: boolean;
  mergeSameEdges?: boolean;
  labels?: Partial<SimpleBlockDiagramLabels>;
};

const GraphAndZoomContainer = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
`;

const ButtonGroupAlignedToTopRightCorner = styled.div`
  position: absolute;
  top: ${s8};
  right: ${s8};
  gap: ${s8};
  display: flex;
  z-index: 1;
  width: fit-content;
  background-color: transparent;
`;

const ZOOM_AMOUNT = 1.2;

const WithZoomControls = ({
  children,
  graphComponent,
  zoomInLabel = 'Zoom in',
  zoomOutLabel = 'Zoom out',
}: {
  graphComponent: RefObject<GraphComponent | null>;
  children: ReactNode;
  zoomInLabel?: string;
  zoomOutLabel?: string;
}) => (
  <GraphAndZoomContainer>
    {children}
    <ButtonGroupAlignedToTopRightCorner>
      <GhostButton
        data-tooltip-text={zoomOutLabel}
        onClick={() =>
          graphComponent.current?.executeCommand(
            Command.DECREASE_ZOOM,
            ZOOM_AMOUNT
          )
        }
      >
        <ZoomOutIcon />
      </GhostButton>
      <GhostButton
        data-tooltip-text={zoomInLabel}
        onClick={() =>
          graphComponent.current?.executeCommand(
            Command.INCREASE_ZOOM,
            ZOOM_AMOUNT
          )
        }
      >
        <ZoomInIcon />
      </GhostButton>
    </ButtonGroupAlignedToTopRightCorner>
  </GraphAndZoomContainer>
);

type ExpandCollapseAllControlsProps = {
  expandCollapseAllButtonConfig: ButtonConfig[];
};
const ExpandCollapseAllControls = ({
  expandCollapseAllButtonConfig,
}: ExpandCollapseAllControlsProps) => (
  <>
    {expandCollapseAllButtonConfig.map(option => (
      <SettingsBarButton key={option.id} {...option} />
    ))}
  </>
);

const ConnectedExpandCollapseAllControls = connect(
  ExpandCollapseAllControls,
  collapseExpandAllGroupsButtonProps$
);

const LeftMenuContainer = styled.div`
  position: absolute;
  top: 20px;
  left: 32px;
  display: flex;
  gap: 8px;
  z-index: 1;
`;

const GraphWrapper = ({
  graph,
  nodes,
  edges,
  inputMode,
  className,
  layoutOrientation,
  defaultGroupingCollapseState,
  edgeGrouping,
  focusedItemId,
  centeredItemId,
  onItemFocus,
  onItemDoubleClick,
  onCanvasClick,
  registerVisualizationContainer,
  unregisterVisualizationContainer,
  registerGraphComponent,
  unregisterGraphComponent,
  animateLayout,
  hasZoomControls,
  lightenDarkColors,
  showLeftMenu,
  mergeSameEdges,
  labels,
}: Props) => {
  const graphComponentNode = useRef<HTMLDivElement>(null);
  const graphComponent = useRef<GraphComponent | null>(null);
  const nodeRef = useRef<SimpleBlockDiagramNode[]>([]);
  const edgeRef = useRef<SimpleBlockDiagramEdge[]>([]);
  const layoutOrientationRef = useRef<LayoutOrientation>(layoutOrientation);
  const defaultGroupingCollapseStateRef = useRef<GroupingCollapseState>(
    GroupingCollapseState.EXPAND_ALL
  );
  const prevEdgeGrouping = useRef<boolean | undefined>(edgeGrouping);
  const edgeCountMap = useRef<Map<IEdge, number>>(new Map());
  const [currentItemChangedFlag, setCurrentItemChangedFlag] = useState(0);

  // Initialize the GraphComponent on mount
  useEffectOnce(() => {
    if (!graphComponentNode.current) return;
    if (graphComponent.current) return;
    graphComponent.current = new GraphComponent(graphComponentNode.current);
    graphComponent.current.mouseWheelZoomFactor = 1.1;
    registerVisualizationContainer?.(graphComponentNode.current);
    registerGraphComponent?.(graphComponent.current);
    // Move the highlight and selection groups back, so they don't overlap labels
    graphComponent.current?.renderTree.highlightGroup.toBack();
    graphComponent.current?.renderTree.selectionGroup.toBack();

    // Move the nodes forward along the z axis so that the expand/collapse buttons are above the edges.
    graphComponent.current.graphModelManager.hierarchicalNestingPolicy =
      HierarchicalNestingPolicy.GROUP_NODES;

    return () => {
      if (!graphComponent.current) return;
      unregisterVisualizationContainer?.();
      unregisterGraphComponent?.();
      graphComponent.current.cleanUp();
    };
  });

  const filterEdgeDuplicates = useCallback(() => {
    const graphEntity = graphComponent?.current?.graph;

    if (
      !mergeSameEdges ||
      !graph ||
      !graphEntity ||
      !(graphEntity instanceof FilteredGraphWrapper)
    ) {
      return;
    }

    edgeCountMap.current = getEdgeCountMap(graph.edges);
    graphEntity.edgePredicateChanged?.();
    updateEdgeLabels(graphEntity, edges, edgeCountMap.current);
  }, [graph, edges, mergeSameEdges]);

  const collapseAllLabel = labels?.collapseAll;
  const expandAllLabel = labels?.expandAll;
  const updateExpandCollapseAllButtons = useCallback(() => {
    dispatchAction(
      collapseExpandAllGroupsButtonsPropsChange({
        expandCollapseAllButtonConfig: translateGraphPropsToButtonConfigProps({
          graph,
          graphComponent: graphComponent.current,
          edgeGrouping,
          layoutOrientation,
          edges,
          filterEdgeDuplicates,
          collapseAllLabel,
          expandAllLabel,
        }),
      })
    );
  }, [
    graph,
    edgeGrouping,
    layoutOrientation,
    edges,
    filterEdgeDuplicates,
    collapseAllLabel,
    expandAllLabel,
  ]);

  // Re-dispatch the button config whenever the callback reference changes
  // (e.g. when locale changes mid-session and the labels are different),
  // so collapse/expand button labels update immediately instead of going
  // stale until the next graph structural or layout change.
  useEffect(() => {
    updateExpandCollapseAllButtons();
  }, [updateExpandCollapseAllButtons]);

  useEffect(() => {
    if (!graph) return;
    graph.foldingView?.addEventListener(
      'group-collapsed',
      updateExpandCollapseAllButtons
    );
    graph.foldingView?.addEventListener(
      'group-expanded',
      updateExpandCollapseAllButtons
    );

    return () => {
      graph.foldingView?.removeEventListener(
        'group-collapsed',
        updateExpandCollapseAllButtons
      );
      graph.foldingView?.removeEventListener(
        'group-expanded',
        updateExpandCollapseAllButtons
      );
    };
  }, [graph, updateExpandCollapseAllButtons]);

  const handleItemClicked = (args: ItemClickedEventArgs<IModelItem>) => {
    const { item } = args;
    if (!item) return;
    if (!(item instanceof ILabel) || !item.owner) return;
    const graphElement = item.owner;
    if (!(graphElement instanceof IEdge) && !(graphElement instanceof INode)) {
      return;
    }
    if (!(inputMode instanceof GraphViewerInputMode)) return;
    inputMode.setCurrentItem(graphElement);
  };

  const handleItemDoubleClicked = (args: ItemClickedEventArgs<IModelItem>) => {
    const { item } = args;
    if (!item) return;
    const graphElement =
      item instanceof ILabel && item.owner ? item.owner : item;
    if (!(graphElement instanceof IEdge) && !(graphElement instanceof INode)) {
      return;
    }
    if (!graphElement || !graphElement.tag || !onItemDoubleClick) return;
    onItemDoubleClick(graphElement.tag);
    args.handled = true;
  };

  const getElementId = (item: IModelItem): string => {
    if (item instanceof ILabel) {
      return item.tag?.elementId ?? item.owner?.tag.elementId;
    }
    return item.tag.elementId;
  };

  const handleHoveredItemChanged = (args: HoveredItemChangedEventArgs) => {
    if (!graphComponent.current || !graph) return;
    const component = graphComponent.current;
    const { highlights } = component;
    highlights.clear();
    const { item } = args;
    if (!item) return;
    const elementId = getElementId(item);
    const graphElements = getGraphItemsById(graph, elementId);

    if (
      graphElements.every(
        graphElement =>
          graphElement instanceof IEdge ||
          graphElement instanceof INode ||
          graphElement instanceof ILabel
      )
    ) {
      graphElements?.forEach(graphElement => highlights.add(graphElement));
    }
  };

  const handleCanvasClicked = () => {
    if (!onCanvasClick) return;
    onCanvasClick();
  };

  const handleGroupCollapsedExpanded = (evt: InputModeItemEventArgs<INode>) => {
    const canvasGraphComponent = graphComponent.current;
    if (!canvasGraphComponent) return;
    filterEdgeDuplicates();

    calculateAndApplyIncrementalHierarchicLayout({
      graphComponent: canvasGraphComponent,
      layoutOrientation,
      edgeGrouping,
      animateViewport: true,
      updateContentBounds: true,
      zoomToNodes: [evt.item],
    });
  };

  // Update props
  useEffectOnce(() => {
    if (!graphComponent.current || !graph) return;
    const component = graphComponent.current;
    component.graph = mergeSameEdges
      ? new FilteredGraphWrapper(
          graph,
          () => true,
          (edge: IEdge) => Boolean(edgeCountMap.current.get(edge))
        )
      : graph;
    component.inputMode = inputMode;
    component.horizontalScrollBarPolicy = ScrollBarVisibility.HIDDEN;
    component.verticalScrollBarPolicy = ScrollBarVisibility.HIDDEN;

    // enable tooltips
    initializeTooltips(component);

    addGroupCollapsedListener(component, args =>
      resetNodeToDefaultCollapsedSize(component.graph, args.item)
    );

    return () => {
      if (component?.graph instanceof FilteredGraphWrapper) {
        component?.graph?.dispose();
      }
    };
  });

  useEffect(() => {
    if (
      graph &&
      graphComponent.current &&
      !(isEqual(nodeRef.current, nodes) && isEqual(edgeRef.current, edges))
    ) {
      graph.clear();
      nodeRef.current = nodes;
      edgeRef.current = edges;
      populateGraph(graph, nodes, edges, lightenDarkColors);
      filterEdgeDuplicates();
      // When yFiles applies a layout with animation it will scale the graph to
      // fit the viewport. This happens asynchronously and will interfere with
      // manually setting the viewport center and zoom level.
      const shouldAnimate =
        animateLayout !== false && centeredItemId === undefined;
      updateExpandCollapseAllButtons();
      calculateAndApplyInitialHierarchicLayout(
        graphComponent.current,
        layoutOrientation,
        edgeGrouping,
        shouldAnimate
      );
      if (centeredItemId === undefined) {
        if (!shouldAnimate) graphComponent.current.fitGraphBounds();
        return;
      }
      centerViewportOnFocusedItem(graphComponent.current, centeredItemId);
    }
  }, [
    graph,
    nodes,
    edges,
    layoutOrientation,
    centeredItemId,
    animateLayout,
    lightenDarkColors,
    updateExpandCollapseAllButtons,
    edgeGrouping,
    filterEdgeDuplicates,
  ]);

  useEffect(() => {
    if (
      graph &&
      graphComponent.current &&
      prevEdgeGrouping.current &&
      edgeGrouping &&
      !isEqual(
        defaultGroupingCollapseStateRef.current,
        defaultGroupingCollapseState
      )
    ) {
      defaultGroupingCollapseStateRef.current = defaultGroupingCollapseState;
      const collapseGroupsArgs = {
        graph,
        graphComponent: graphComponent.current,
        edgeGrouping,
        layoutOrientation,
        filterEdgeDuplicates,
      };
      const expandGroupsArgs = {
        ...collapseGroupsArgs,
        viewGroups: getCollapsedViewGroups(graph),
      };
      if (defaultGroupingCollapseState === GroupingCollapseState.COLLAPSE_ALL) {
        collapseAllGroups(collapseGroupsArgs);
        calculateAndApplyIncrementalHierarchicLayout({
          graphComponent: graphComponent.current,
          layoutOrientation,
          edgeGrouping,
        });
      } else {
        expandAllGroups(expandGroupsArgs);
      }
    }
  }, [
    graph,
    edgeGrouping,
    prevEdgeGrouping,
    layoutOrientation,
    filterEdgeDuplicates,
    defaultGroupingCollapseState,
  ]);

  useEffect(() => {
    if (!graphComponent.current || !graph) return;
    const component = graphComponent.current;
    component.inputMode = inputMode;

    // Reregister all listeners on update
    addItemDoubleClickedListener(component, handleItemDoubleClicked);
    addItemClickedListener(component, handleItemClicked);
    addHoveredItemChangedListener(component, handleHoveredItemChanged);
    addCanvasClickedListener(component, handleCanvasClicked);
    addGroupCollapsedListener(component, handleGroupCollapsedExpanded);
    addGroupExpandedListener(component, handleGroupCollapsedExpanded);
    return () => {
      removeItemDoubleClickedListener(component, handleItemDoubleClicked);
      removeItemClickedListener(component, handleItemClicked);
      removeHoveredItemChangedListener(component, handleHoveredItemChanged);
      removeCanvasClickedListener(component, handleCanvasClicked);
      removeGroupCollapsedListener(component, handleGroupCollapsedExpanded);
      removeGroupExpandedListener(component, handleGroupCollapsedExpanded);
    };
  });

  useEffect(() => {
    if (!graphComponent.current) return;
    // When yFiles applies a layout with animation it will scale the graph to
    // fit the viewport. This happens asynchronously and will interfere with
    // manually setting the viewport center and zoom level.
    const edgeGroupingChanged = prevEdgeGrouping.current !== edgeGrouping;
    const layoutOrientationChanged =
      layoutOrientationRef.current !== layoutOrientation;
    const noCenteredItem = centeredItemId === undefined;
    const shouldAnimate = noCenteredItem || edgeGroupingChanged;

    if (!layoutOrientationChanged && !edgeGroupingChanged) {
      return;
    }

    if (edgeGroupingChanged) {
      calculateAndApplyIncrementalHierarchicLayout({
        graphComponent: graphComponent.current,
        layoutOrientation,
        edgeGrouping,
        edges,
        getEdgeCountMap: edgeCountMap.current,
      });
    } else {
      calculateAndApplyInitialHierarchicLayout(
        graphComponent.current,
        layoutOrientation,
        edgeGrouping,
        shouldAnimate
      );
    }

    if (edgeGroupingChanged) {
      prevEdgeGrouping.current = edgeGrouping;
      updateExpandCollapseAllButtons();
    }

    if (centeredItemId && layoutOrientationChanged) {
      layoutOrientationRef.current = layoutOrientation;
      centerViewportOnFocusedItem(graphComponent.current, centeredItemId);
    }
  }, [
    layoutOrientation,
    centeredItemId,
    edgeGrouping,
    updateExpandCollapseAllButtons,
    edges,
  ]);

  useEffect(() => {
    if (focusedItemId === undefined) return;
    const component = graphComponent.current;
    if (!component) return;
    selectGraphItemsById(component, focusedItemId ?? null);

    const currentItemChangedListener = (
      _: EventArgs,
      graphComponent: GraphComponent
    ) => {
      const { currentItem } = graphComponent;
      setCurrentItemChangedFlag(currentItemChangedFlag === 1 ? 0 : 1);
      if (!onItemFocus || !currentItem) return;
      onItemFocus(currentItem.tag);
    };

    component.addEventListener(
      'current-item-changed',
      currentItemChangedListener
    );
    return () => {
      component.removeEventListener(
        'current-item-changed',
        currentItemChangedListener
      );
    };
  }, [focusedItemId, currentItemChangedFlag, onItemFocus]);

  return (
    <>
      {showLeftMenu ? (
        <LeftMenuContainer>
          <ConnectedExpandCollapseAllControls />
        </LeftMenuContainer>
      ) : null}
      {hasZoomControls ? (
        <WithZoomControls
          graphComponent={graphComponent}
          zoomInLabel={labels?.zoomIn}
          zoomOutLabel={labels?.zoomOut}
        >
          <div ref={graphComponentNode} className={className} />
        </WithZoomControls>
      ) : (
        <div ref={graphComponentNode} className={className} />
      )}
    </>
  );
};

export { GraphWrapper };
